import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { locale } from "@/lib/format";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { api, resolveFileUrl } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/formatDate";
import { SearchableSelect } from "@/components/SearchableSelect";
import { hardConfirm } from "@/lib/confirmToast";
import { Skeleton, SkeletonCard, SkeletonLine } from "@/components/Skeleton";
import { useAppBase } from "@/hooks/useAppBase";
import { ArrowLeft, Save, Trash2, Calendar, Receipt, Sparkles, ShieldCheck, User, Mail, Phone, Cake, MapPin, Tag as TagIcon, Plus, X, Camera, Upload, Pencil, CloudUpload, Check, MessageSquare, Activity, Facebook, Instagram, Send, Zap, MoveHorizontal, StickyNote, ChevronDown, ChevronRight, Globe, Gift, RotateCcw, Store } from "lucide-react";
import { WhatsAppChat } from "@/components/WhatsAppChat";
import { ClientAcquisition } from "@/components/ClientAcquisition";

const SOURCES = [
  { value: "walk-in", label: "Passaggio" },
  { value: "ads", label: "Contatto ADS" },
  { value: "referral", label: "Passaparola" },
  { value: "website", label: "Sito web" },
  { value: "instagram", label: "Instagram" },
  { value: "google", label: "Google" },
];
const SOURCE_LABEL = Object.fromEntries(SOURCES.map((s) => [s.value, s.label]));

const TABS = [
  { key: "profile", label: "clientDetail.tabs.profile", icon: User },
  { key: "activity", label: "clientDetail.tabs.activity", icon: Activity },
  { key: "technical", label: "clientDetail.tabs.technical", icon: Sparkles },
  { key: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  { key: "appointments", label: "clientDetail.tabs.appointments", icon: Calendar },
  { key: "invoices", label: "clientDetail.tabs.revenue", icon: Receipt, tenantOnly: true },
  { key: "packages", label: "clientDetail.tabs.packages", icon: Gift, tenantOnly: true },
  { key: "consents", label: "clientDetail.tabs.privacy", icon: ShieldCheck },
];

const eur = (n) => `€ ${(n ?? 0).toLocaleString(locale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// fmtDate + fmtDateTime imported from @/lib/formatDate — shared everywhere

// Bottone (solo pannello admin) per convertire un contatto HQ in scheda Cliente.
function ConvertToClientButton({ clientId, name }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [salons, setSalons] = useState([]);
  const [salonId, setSalonId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) api.get("/admin/salons-lookup").then((r) => setSalons(r.data?.salons || [])).catch(() => {});
  }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/agency-clients/from-hq-client/${clientId}`, null, { params: salonId ? { salon_tenant_id: salonId } : {} });
      toast.success("Convertito in cliente");
      nav(`/admin/clienti/${r.data.id}`);
    } catch { toast.error("Errore conversione"); } finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="convert-to-client-btn" onClick={() => setOpen(true)} className="sp-btn-ghost inline-flex items-center gap-2">
        <Store className="h-4 w-4" /> Converti in cliente
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="sp-card p-6 w-full max-w-lg space-y-3" data-testid="convert-to-client-modal">
            <h3 className="font-display text-xl font-black inline-flex items-center gap-2"><Store className="h-5 w-5 text-[color:var(--sp-brand)]" /> Converti in cliente</h3>
            <p className="text-sm text-[color:var(--sp-text-secondary)]">Crea la scheda cliente per <strong>{name}</strong>. Collega il salone Memento (o fallo dopo).</p>
            <select value={salonId} onChange={(e) => setSalonId(e.target.value)} data-testid="convert-salon-select" className="sp-input">
              <option value="">Collego dopo…</option>
              {salons.map((s) => <option key={s.id} value={s.id} disabled={s.already_linked}>{s.name}{s.already_linked ? " (già collegato)" : ""}</option>)}
            </select>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-bold hover:bg-[color:var(--sp-subtle)]">Annulla</button>
              <button onClick={submit} disabled={busy} data-testid="convert-confirm-btn" className="px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">{busy ? "Converto…" : "Crea cliente"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function ClientDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const base = useAppBase();
  const isAdmin = base === "/admin";
  // Nel pannello admin (agenzia) i saloni sono contatti B2B: incassi e
  // percorsi del salone non hanno significato → nascondiamo quelle tab.
  const visibleTabs = useMemo(
    () => TABS.filter((t) => !(t.tenantOnly && isAdmin)),
    [isAdmin],
  );
  const validTabKeys = useMemo(() => visibleTabs.map((t) => t.key), [visibleTabs]);
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const initialTab = requested && validTabKeys.includes(requested) ? requested : "profile";
  const [tab, setTab] = useState(initialTab);
  // Se l'utente ha una tab admin-hidden in state (es. deep link), redirect a profile
  useEffect(() => {
    if (!validTabKeys.includes(tab)) setTab("profile");
  }, [validTabKeys, tab]);
  const [data, setData] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoSaveState, setAutoSaveState] = useState("idle"); // idle | pending | saved
  const autosaveTimer = useRef(null);
  const isFirstFormLoad = useRef(true);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [pendingPhoto, setPendingPhoto] = useState(null); // { url, note }
  const [adsInfo, setAdsInfo] = useState(null);
  const [showAdsInfo, setShowAdsInfo] = useState(false);
  const [editingPhotoIdx, setEditingPhotoIdx] = useState(null);
  const [editingPhotoNote, setEditingPhotoNote] = useState("");

  const load = async () => {
    const [h, tg, sv, st] = await Promise.all([
      api.get(`/clients/${id}/history`),
      api.get("/tags"),
      api.get("/services"),
      api.get("/staff"),
    ]);
    setData(h.data);
    setAvailableTags(tg.data);
    setServices(sv.data);
    setStaff(st.data);
    setForm(normalizeForm(h.data.client));
    setPhotos(h.data.client.photos || []);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  // Autosave with debounce (2s) — kick in only after initial form load
  useEffect(() => {
    if (!form) return;
    if (isFirstFormLoad.current) { isFirstFormLoad.current = false; return; }
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    setAutoSaveState("pending");
    autosaveTimer.current = setTimeout(() => {
      save({ silent: true });
    }, 2000);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [form]); // eslint-disable-line

  // Cmd/Ctrl+S to save immediately
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // no deps → always latest closure

  function normalizeForm(c) {
    return {
      name: c.name || "",
      email: c.email || "",
      phone: c.phone || "",
      birthday: c.birthday || "",
      address: c.address || "",
      tags: c.tags || [],
      notes: c.notes || "",
      technical: {
        hair_type: c.technical?.hair_type || "",
        scalp: c.technical?.scalp || "",
        allergies: c.technical?.allergies || "",
        base_color: c.technical?.base_color || "",
        current_formula: c.technical?.current_formula || "",
        products_used: c.technical?.products_used || "",
        journey: c.technical?.journey || "",
      },
      consents: {
        privacy: !!c.consents?.privacy,
        marketing_email: !!c.consents?.marketing_email,
        marketing_sms: !!c.consents?.marketing_sms,
        photo: !!c.consents?.photo,
        signed_at: c.consents?.signed_at || null,
      },
    };
  }

  const save = async ({ silent = false } = {}) => {
    if (!form?.name?.trim()) { if (!silent) toast.error("Il nome è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        email: form.email || null,
        birthday: form.birthday || null,
        address: form.address || null,
      };
      // Notes are managed via the unified activities endpoint — never save
      // stale form.notes here or we'd overwrite fresh backend state.
      delete payload.notes;
      // Auto-stamp consents.signed_at when any consent flipped from false to signed
      const anyConsent = payload.consents.privacy || payload.consents.marketing_email || payload.consents.marketing_sms || payload.consents.photo;
      if (anyConsent && !payload.consents.signed_at) {
        payload.consents.signed_at = new Date().toISOString();
      }
      if (!anyConsent) payload.consents.signed_at = null;
      await api.patch(`/clients/${id}`, payload);
      if (!silent) toast.success("Cliente aggiornato");
      setAutoSaveState("saved");
      setTimeout(() => setAutoSaveState("idle"), 1500);
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || "Errore");
      setAutoSaveState("idle");
    }
    finally { setSaving(false); }
  };

  // Photo history handlers
  const onPickPhoto = () => fileInputRef.current?.click();
  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingPhoto({ url: up.path, note: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload fallito"); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  const confirmAddPhoto = async () => {
    if (!pendingPhoto) return;
    try {
      await api.post(`/clients/${id}/photos`, pendingPhoto);
      toast.success("Foto aggiunta allo storico");
      setPendingPhoto(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const removePhoto = async (idx) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    try {
      await api.delete(`/clients/${id}/photos/${idx}`);
      toast.success("Foto rimossa");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const startEditNote = (idx) => {
    setEditingPhotoIdx(idx);
    setEditingPhotoNote(photos[idx]?.note || "");
  };
  const savePhotoNote = async () => {
    if (editingPhotoIdx == null) return;
    const p = photos[editingPhotoIdx];
    try {
      await api.patch(`/clients/${id}/photos/${editingPhotoIdx}`, { url: p.url, note: editingPhotoNote });
      toast.success("Nota aggiornata");
      setEditingPhotoIdx(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const remove = async () => {
    const ok = await hardConfirm({
      title: "Eliminare definitivamente questo cliente?",
      description: "L'operazione non è reversibile e cancellerà anche lo storico associato.",
      confirmLabel: "Elimina",
      danger: true,
    });
    if (!ok) return;
    await api.delete(`/clients/${id}`);
    toast.success("Cliente eliminato");
    nav(`${base}/clients`);
  };

  const toggleTag = (name) => {
    const has = form.tags.includes(name);
    setForm({ ...form, tags: has ? form.tags.filter((t) => t !== name) : [...form.tags, name] });
  };

  const svcName = (id) => services.find((s) => s.id === id)?.name || "";
  const staffName = (id) => staff.find((s) => s.id === id)?.name || "—";

  const nextBirthday = useMemo(() => {
    if (!form?.birthday) return null;
    const [y, m, d] = form.birthday.split("-").map(Number);
    if (!m || !d) return null;
    const now = new Date();
    let next = new Date(now.getFullYear(), m - 1, d);
    if (next < new Date(now.getFullYear(), now.getMonth(), now.getDate())) next = new Date(now.getFullYear() + 1, m - 1, d);
    const days = Math.ceil((next - new Date()) / (1000 * 60 * 60 * 24));
    const age = y ? new Date().getFullYear() - y + (next.getFullYear() > new Date().getFullYear() ? 0 : 0) : null;
    return { days, next, age };
  }, [form?.birthday]);

  if (!data || !form) return (
    <div className="space-y-6" data-testid="client-detail-loading">
      <div className="space-y-2">
        <SkeletonLine w="w-24" h="h-3" />
        <SkeletonLine w="w-64" h="h-10" />
        <SkeletonLine w="w-96" h="h-3" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
      <SkeletonLine w="w-full" h="h-64" />
    </div>
  );

  const s = data.stats;

  return (
    <div data-testid="client-detail" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to={`${base}/clients`} className="inline-flex items-center gap-1 text-xs text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)]">
            <ArrowLeft className="h-3 w-3" /> {t("clientDetail.backToClients")}
          </Link>
          <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight mt-2">{form.name || "Cliente"}</h1>
          <div className="flex items-center gap-3 flex-wrap mt-2 text-sm text-[color:var(--sp-text-secondary)]">
            {form.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{form.email}</span>}
            {form.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{form.phone}</span>}
            {form.birthday && <span className="inline-flex items-center gap-1"><Cake className="h-3 w-3" />{fmtDate(form.birthday)}{nextBirthday && nextBirthday.days <= 30 && <em className="ml-1 text-[color:var(--sp-brand)]">tra {nextBirthday.days}gg 🎂</em>}</span>}
            {data.client?.acquisition_source === "meta_ads" && (
              <span className="relative inline-block">
                <button
                  type="button"
                  data-testid="client-ads-badge"
                  onClick={async () => {
                    if (showAdsInfo) { setShowAdsInfo(false); return; }
                    setShowAdsInfo(true);
                    if (!adsInfo) {
                      try {
                        const r = await api.get(`/meta/leads/by-client/${id}`);
                        setAdsInfo(r.data);
                      } catch { setAdsInfo({ form_name: null, acquired_at: null }); }
                    }
                  }}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] text-xs font-bold hover:bg-[color:var(--sp-brand)]/20 transition-colors cursor-pointer">
                  <Facebook className="h-3 w-3" /> Lead da Meta Ads
                </button>
                {showAdsInfo && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowAdsInfo(false)} />
                    <div data-testid="client-ads-info-popover"
                         className="absolute z-30 mt-2 left-0 w-64 rounded-xl border border-[color:var(--sp-border)] bg-white shadow-xl p-3 text-left">
                      <div className="text-[11px] font-black uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-2">{t("clientDetail.acquisition")}</div>
                      <dl className="space-y-1.5 text-xs">
                        <div className="flex justify-between gap-2"><dt className="text-[color:var(--sp-text-muted)]">Tipo</dt><dd className="font-bold text-right">Lead da Meta Ads</dd></div>
                        <div className="flex justify-between gap-2"><dt className="text-[color:var(--sp-text-muted)]">Form</dt><dd className="font-bold text-right" data-testid="ads-info-form">{adsInfo?.form_name || "—"}</dd></div>
                        <div className="flex justify-between gap-2"><dt className="text-[color:var(--sp-text-muted)]">Data</dt><dd className="font-bold text-right" data-testid="ads-info-date">{adsInfo?.acquired_at ? fmtDateTime(adsInfo.acquired_at) : "—"}</dd></div>
                      </dl>
                    </div>
                  </>
                )}
              </span>
            )}
          </div>
          {form.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {form.tags.filter((t) => t !== "lead_ads").map((t) => {
                const meta = availableTags.find((x) => x.name === t);
                const color = meta?.color || "#06B6D4";
                return (
                  <span key={t} className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: `${color}18`, color }}>#{t}</span>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {autoSaveState === "pending" && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[color:var(--sp-text-muted)]" data-testid="autosave-pending">
              <CloudUpload className="h-3 w-3 animate-pulse" /> {t("common.saving")}
            </span>
          )}
          {autoSaveState === "saved" && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[color:var(--sp-success)]" data-testid="autosave-saved">
              <Check className="h-3 w-3" /> {t("common.savedShort")}
            </span>
          )}
          {isAdmin && <ConvertToClientButton clientId={id} name={form.name} />}
          <button data-testid="client-delete-btn" onClick={remove} className="sp-btn-ghost !text-[color:var(--sp-danger)] !border-[color:var(--sp-danger)]/50 inline-flex items-center gap-2">
            <Trash2 className="h-4 w-4" /> {t("common.delete")}
          </button>
          <button data-testid="client-save-btn" onClick={() => save()} disabled={saving} className="sp-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-tip="Cmd+S">
            <Save className="h-4 w-4" /> {saving ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>

      {/* Acquisizione (read-only, dati di sistema) */}
      <ClientAcquisition clientId={id} />

      {/* Stats — compact horizontal strip */}
      <section className="sp-card p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="client-stats">
        <StatCompact label={t("inbox.appointments")} value={s.total_appointments} sub={t("clientDetail.stats.completed")} />
        <StatCompact label={t("clientDetail.stats.lastAppt")} value={fmtDate(s.last_appointment)} sub={t("clientDetail.stats.mostRecent")} />
        <StatCompact label={t("clientDetail.stats.frequency")} value={s.frequency_days != null ? t("clientDetail.stats.days", { n: s.frequency_days }) : "—"} sub={t("clientDetail.stats.frequencySub")} />
        <StatCompact label={t("dashboard.metrics.avgService")} value={eur(s.avg_ticket)} sub={t("clientDetail.stats.receipts", { n: s.invoice_count })} />
        <StatCompact label={t("clientDetail.stats.totalSpent")} value={eur(s.total_spent)} sub="lifetime value" tone="brand" />
      </section>

      {/* Pipeline stage widget */}
      <PipelineStageWidget clientId={data.client.id}
        currentStage={data.client.pipeline_stage}
        onChanged={load} />


      {/* Tabs */}
      <div className="inline-flex flex-wrap gap-1 rounded-full border border-[color:var(--sp-border)] bg-white p-1">
        {visibleTabs.map((T) => (
          <button key={T.key} data-testid={`tab-${T.key}`} onClick={() => setTab(T.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-2 transition-colors ${tab === T.key ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
            <T.icon className="h-3.5 w-3.5" />
            {t(T.label)}
          </button>
        ))}
      </div>

      {/* Panels */}
      {tab === "profile" && (
        <section className="sp-card p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("clients.cols.name")}><input data-testid="field-name" required className="sp-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label={t("auth.email")}><input data-testid="field-email" type="email" className="sp-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label={t("clients.phone")}><input data-testid="field-phone" className="sp-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label={t("clientDetail.birthday")}><input data-testid="field-birthday" type="date" className="sp-input" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} /></Field>
            <Field label={t("clientDetail.address")} className="md:col-span-2"><input data-testid="field-address" className="sp-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold inline-flex items-center gap-1.5"><TagIcon className="h-3.5 w-3.5" /> Tag</label>
              <Link to={`${base}/tags`} className="text-xs text-[color:var(--sp-brand)] font-bold hover:underline">{t("clientDetail.manageTags")}</Link>
            </div>
            {availableTags.length === 0 ? (
              <p className="text-xs text-[color:var(--sp-text-muted)] mt-2">
                Nessun tag disponibile. <Link to={`${base}/tags`} className="underline">Creane uno</Link>.
              </p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                {availableTags.map((t) => {
                  const on = form.tags.includes(t.name);
                  return (
                    <button key={t.id} type="button" data-testid={`tag-toggle-${t.name}`} onClick={() => toggleTag(t.name)}
                            className="px-3 py-1.5 rounded-full text-xs font-bold border transition-colors"
                            style={on
                              ? { background: t.color, borderColor: t.color, color: "#fff" }
                              : { borderColor: `${t.color}55`, background: `${t.color}10`, color: t.color }}>
                      {on ? "✓ " : ""}#{t.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <UnifiedNotes clientId={id} />
        </section>
      )}

      {tab === "activity" && <ActivityTimeline clientId={id} />}

      {tab === "technical" && (
        <section className="sp-card p-6 space-y-5">
          <p className="text-xs text-[color:var(--sp-text-muted)]">Info tricologiche e percorso in salone. Utile per garantire continuità tra collaboratori.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Tipo di capello"><input data-testid="field-hair-type" placeholder="liscio / mosso / riccio / crespo" className="sp-input" value={form.technical.hair_type} onChange={(e) => setForm({ ...form, technical: { ...form.technical, hair_type: e.target.value } })} /></Field>
            <Field label="Cuoio capelluto"><input data-testid="field-scalp" placeholder="normale / grasso / secco / sensibile" className="sp-input" value={form.technical.scalp} onChange={(e) => setForm({ ...form, technical: { ...form.technical, scalp: e.target.value } })} /></Field>
            <Field label="Allergie / intolleranze" className="md:col-span-2"><input data-testid="field-allergies" placeholder="es. PPD, ammoniaca…" className="sp-input" value={form.technical.allergies} onChange={(e) => setForm({ ...form, technical: { ...form.technical, allergies: e.target.value } })} /></Field>
            <Field label="Colore base naturale"><input data-testid="field-base-color" placeholder="es. 5N, 6.3" className="sp-input" value={form.technical.base_color} onChange={(e) => setForm({ ...form, technical: { ...form.technical, base_color: e.target.value } })} /></Field>
            <Field label="Formula attuale"><input data-testid="field-formula" placeholder="es. 6.34 + 20vol" className="sp-input" value={form.technical.current_formula} onChange={(e) => setForm({ ...form, technical: { ...form.technical, current_formula: e.target.value } })} /></Field>
            <Field label="Prodotti utilizzati" className="md:col-span-2"><input data-testid="field-products" placeholder="marca linea shampoo/maschera…" className="sp-input" value={form.technical.products_used} onChange={(e) => setForm({ ...form, technical: { ...form.technical, products_used: e.target.value } })} /></Field>
            <Field label="Percorso in salone / storico trattamenti" className="md:col-span-2">
              <textarea data-testid="field-journey" placeholder="Annotazioni del parrucchiere: obiettivi capello, cambi colore, trattamenti chimici, decolorazioni, tempi di posa, reazioni…" className="sp-input min-h-[140px]" value={form.technical.journey} onChange={(e) => setForm({ ...form, technical: { ...form.technical, journey: e.target.value } })} />
            </Field>
          </div>

          {/* Photo history */}
          <div className="pt-4 border-t border-[color:var(--sp-border)]">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-display text-lg font-black inline-flex items-center gap-2"><Camera className="h-4 w-4 text-[color:var(--sp-brand)]" /> Foto storiche</h3>
                <p className="text-xs text-[color:var(--sp-text-muted)] mt-0.5">Prima/dopo, ispirazioni, riferimenti tecnici. Aggiungi una nota descrittiva.</p>
              </div>
              <button data-testid="photo-add-btn" onClick={onPickPhoto} disabled={uploading}
                      className="sp-btn-ghost inline-flex items-center gap-2 text-xs disabled:opacity-50">
                <Upload className="h-3.5 w-3.5" /> {uploading ? "Caricamento…" : "Aggiungi foto"}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileSelected} data-testid="photo-file-input" />
            </div>

            {photos.length === 0 && !pendingPhoto ? (
              <div className="p-6 rounded-lg border-2 border-dashed border-[color:var(--sp-border)] text-center text-sm text-[color:var(--sp-text-muted)]">
                Nessuna foto ancora. Aggiungi la prima per iniziare lo storico visivo.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {pendingPhoto && (
                  <div className="rounded-lg border-2 border-[color:var(--sp-brand)] p-3 bg-[color:var(--sp-brand)]/5">
                    <img src={resolveFileUrl(pendingPhoto.url)} alt="Nuova foto" className="w-full aspect-square object-cover rounded" />
                    <textarea
                      data-testid="pending-photo-note"
                      autoFocus
                      placeholder="Aggiungi una nota (es. Colore prima seduta, formula 6.34)…"
                      className="sp-input mt-2 min-h-[60px] text-xs"
                      value={pendingPhoto.note || ""}
                      onChange={(e) => setPendingPhoto({ ...pendingPhoto, note: e.target.value })}
                    />
                    <div className="mt-2 flex gap-2">
                      <button data-testid="pending-photo-cancel" onClick={() => setPendingPhoto(null)} className="sp-btn-ghost text-xs flex-1">Annulla</button>
                      <button data-testid="pending-photo-save" onClick={confirmAddPhoto} className="sp-btn-primary text-xs flex-1">Salva foto</button>
                    </div>
                  </div>
                )}
                {photos.map((p, i) => (
                  <div key={i} data-testid={`photo-item-${i}`} className="rounded-lg border border-[color:var(--sp-border)] p-2 bg-white group relative overflow-hidden">
                    <img src={resolveFileUrl(p.url)} alt={p.note || `Foto ${i+1}`} className="w-full aspect-square object-cover rounded" />
                    {editingPhotoIdx === i ? (
                      <div className="mt-2">
                        <textarea data-testid={`photo-note-edit-${i}`} autoFocus className="sp-input min-h-[50px] text-xs" value={editingPhotoNote} onChange={(e) => setEditingPhotoNote(e.target.value)} />
                        <div className="mt-1 flex gap-1">
                          <button onClick={() => setEditingPhotoIdx(null)} className="sp-btn-ghost text-xs flex-1">Annulla</button>
                          <button data-testid={`photo-note-save-${i}`} onClick={savePhotoNote} className="sp-btn-primary text-xs flex-1">{t("common.save")}</button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 flex items-start gap-1">
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] text-[color:var(--sp-text-secondary)] line-clamp-2">{p.note || <em className="opacity-60">— senza nota —</em>}</p>
                          {p.created_at && <p className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">{fmtDate(p.created_at)}</p>}
                        </div>
                        <button data-testid={`photo-edit-note-${i}`} onClick={() => startEditNote(i)} className="p-1 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)]" title="Modifica nota"><Pencil className="h-3 w-3" /></button>
                        <button data-testid={`photo-delete-${i}`} onClick={() => removePhoto(i)} className="p-1 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]" title="Elimina"><X className="h-3 w-3" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {tab === "whatsapp" && (
        <WhatsAppChat clientId={id} clientName={form.name} clientPhone={form.phone} />
      )}

      {tab === "appointments" && (
        <section className="sp-card overflow-hidden">
          {data.appointments.length === 0 ? (
            <div className="p-12 text-center text-[color:var(--sp-text-muted)]">Nessun appuntamento registrato per questo cliente.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-[color:var(--sp-subtle)] text-left">
                <tr className="label-eyebrow">
                  <th className="p-4">{t("clientDetail.table.date")}</th>
                  <th className="p-4">{t("clientDetail.table.services")}</th>
                  <th className="p-4">{t("nav.staff")}</th>
                  <th className="p-4">{t("clientDetail.table.status")}</th>
                  <th className="p-4 text-right">{t("clientDetail.table.revenue")}</th>
                </tr>
              </thead>
              <tbody>
                {data.appointments.map((a, i) => {
                  const svcIds = a.service_ids?.length ? a.service_ids : (a.service_id ? [a.service_id] : []);
                  const goToAgenda = () => {
                    const d = a.start ? a.start.slice(0, 10) : "";
                    nav(`${base}/agenda?date=${d}&edit=${a.id}`);
                  };
                  return (
                    <tr key={a.id} data-testid={`appt-history-${a.id}`} className={`${i % 2 ? "bg-[color:var(--sp-bg)]" : ""} hover:bg-[color:var(--sp-subtle)]/60 cursor-pointer transition-colors`}
                        onClick={goToAgenda}>
                      <td className="p-4 font-bold text-sm">{fmtDateTime(a.start)}</td>
                      <td className="p-4 text-sm">{svcIds.map(svcName).filter(Boolean).join(", ") || "—"}</td>
                      <td className="p-4 text-sm">{staffName(a.staff_id)}</td>
                      <td className="p-4"><StatusBadge status={a.status} /></td>
                      <td className="p-4 text-right font-black text-sm">
                        {a.invoice_total != null
                          ? <span className="text-[color:var(--sp-brand)]">{eur(a.invoice_total)}</span>
                          : <span className="text-[color:var(--sp-text-muted)] font-normal text-xs">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      )}

      {tab === "invoices" && (
        <section className="sp-card overflow-hidden">
          {data.invoices.length === 0 ? (
            <div className="p-12 text-center text-[color:var(--sp-text-muted)]">{t("clientDetail.noRevenue")}</div>
          ) : (
            <table className="w-full">
              <thead className="bg-[color:var(--sp-subtle)] text-left">
                <tr className="label-eyebrow">
                  <th className="p-4">{t("clientDetail.table.date")}</th>
                  <th className="p-4">{t("clientDetail.table.number")}</th>
                  <th className="p-4">{t("clientDetail.table.lines")}</th>
                  <th className="p-4">{t("clientDetail.table.payment")}</th>
                  <th className="p-4 text-right">{t("clientDetail.table.total")}</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((i, idx) => (
                  <tr key={i.id} data-testid={`inv-history-${i.id}`} className={idx % 2 ? "bg-[color:var(--sp-bg)]" : ""}>
                    <td className="p-4 text-sm font-bold">{fmtDate(i.created_at)}</td>
                    <td className="p-4 text-sm">{i.number || "—"}</td>
                    <td className="p-4 text-sm text-[color:var(--sp-text-secondary)]">{(i.lines || []).map((l) => l.description || svcName(l.service_id)).filter(Boolean).join(" · ") || "—"}</td>
                    <td className="p-4 text-sm capitalize">{i.payment_method || "—"}</td>
                    <td className="p-4 text-right font-black text-[color:var(--sp-brand)]">{eur(i.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {tab === "packages" && (
        <ClientPackagesSection clientId={id} />
      )}

      {tab === "consents" && (
        <section className="sp-card p-6 space-y-4">
          <p className="text-xs text-[color:var(--sp-text-muted)]">Registra qui i consensi firmati dal cliente. La data di firma viene aggiornata al primo salvataggio con almeno un consenso attivo.</p>
          <ConsentToggle
            label="Informativa privacy (GDPR)"
            description="Il cliente ha ricevuto e accettato l'informativa sul trattamento dati personali."
            checked={form.consents.privacy}
            onChange={(v) => setForm({ ...form, consents: { ...form.consents, privacy: v } })}
            testId="consent-privacy"
          />
          <ConsentToggle
            label="Marketing via email"
            description="Autorizza l'invio di comunicazioni promozionali via email (newsletter, offerte)."
            checked={form.consents.marketing_email}
            onChange={(v) => setForm({ ...form, consents: { ...form.consents, marketing_email: v } })}
            testId="consent-email"
          />
          <ConsentToggle
            label="Marketing via SMS / WhatsApp"
            description="Autorizza l'invio di promemoria e promozioni via SMS o WhatsApp."
            checked={form.consents.marketing_sms}
            onChange={(v) => setForm({ ...form, consents: { ...form.consents, marketing_sms: v } })}
            testId="consent-sms"
          />
          <ConsentToggle
            label="Uso di foto e video"
            description="Autorizza l'utilizzo di foto/video del look sui canali social e materiali del salone."
            checked={form.consents.photo}
            onChange={(v) => setForm({ ...form, consents: { ...form.consents, photo: v } })}
            testId="consent-photo"
          />
          {form.consents.signed_at && (
            <div className="text-xs text-[color:var(--sp-text-muted)] pt-2 border-t border-[color:var(--sp-border)]">
              Ultima firma registrata il <strong>{fmtDateTime(form.consents.signed_at)}</strong>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-sm font-bold">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function StatCard({ label, value, sub, tone }) {
  return (
    <div className="sp-card p-4">
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className={`font-display font-black text-xl mt-2 ${tone === "brand" ? "text-[color:var(--sp-brand)]" : ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">{sub}</div>}
    </div>
  );
}

function StatCompact({ label, value, sub, tone }) {
  return (
    <div className="px-2 min-w-0">
      <div className="label-eyebrow text-[9px]">{label}</div>
      <div className={`font-display font-black text-lg mt-1 truncate ${tone === "brand" ? "text-[color:var(--sp-brand)]" : ""}`} title={String(value)}>{value}</div>
      {sub && <div className="text-[9px] text-[color:var(--sp-text-muted)] truncate">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    scheduled: { label: "Prenotato", color: "#3B82F6" },
    completed: { label: "Completato", color: "#10B981" },
    cancelled: { label: "Cancellato", color: "#EF4444" },
    no_show: { label: "No-show", color: "#F59E0B" },
  };
  const m = map[status] || { label: status || "—", color: "#6B7280" };
  return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: `${m.color}18`, color: m.color }}>{m.label}</span>;
}

function ConsentToggle({ label, description, checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-3 p-3 rounded-lg border border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)]/30 cursor-pointer hover:bg-[color:var(--sp-subtle)] transition-colors">
      <input data-testid={testId} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-[color:var(--sp-brand)]" />
      <div className="flex-1">
        <div className="text-sm font-bold">{label}</div>
        <div className="text-xs text-[color:var(--sp-text-muted)] mt-0.5">{description}</div>
      </div>
    </label>
  );
}

function PipelineStageWidget({ clientId, currentStage, onChanged }) {
  const { t } = useTranslation();
  const base = useAppBase();
  const [stages, setStages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/pipeline/stages").then((r) => setStages(r.data)).catch(() => {});
  }, []);

  const move = async (key) => {
    if (key === currentStage) return;
    setSaving(true);
    try {
      await api.patch(`/pipeline/${clientId}/stage`, { stage: key });
      toast.success("Fase pipeline aggiornata");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const current = stages.find((s) => s.key === currentStage);
  return (
    <section className="sp-card p-4 flex items-center gap-3 flex-wrap" data-testid="client-pipeline-widget">
      <div className="text-xs uppercase tracking-widest text-[color:var(--sp-text-muted)] font-bold">
        {t("clients.pipelineStage")}
      </div>
      {current ? (
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold"
              style={{ background: `${current.color}20`, color: current.color }}
              data-testid="client-pipeline-current">
          <span className="h-2 w-2 rounded-full" style={{ background: current.color }} />
          {current.label}
        </span>
      ) : (
        <span className="text-sm text-[color:var(--sp-text-muted)]">—</span>
      )}
      <div className="flex-1 min-w-[150px]">
        <select
          value={currentStage || ""}
          onChange={(e) => move(e.target.value)}
          disabled={saving}
          className="sp-input"
          data-testid="client-pipeline-select"
        >
          <option value="">{t("clientDetail.unassigned")}</option>
          {stages.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </div>
      <Link to={`${base}/pipeline`} className="text-xs text-[color:var(--sp-brand)] font-bold hover:underline">
        {t("clientDetail.openPipeline")}
      </Link>
    </section>
  );
}


// ============================================================================
// Unified notes — same source as Activity timeline (type=manual)
// ============================================================================
function UnifiedNotes({ clientId }) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clients/${clientId}/activities?type=manual&limit=100`);
      setNotes(r.data.items || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/clients/${clientId}/activities/manual`, { text: t });
      setText("");
      toast.success("Nota aggiunta");
      load();
    } catch (e) {
      toast.error("Errore invio");
    }
    setSending(false);
  };

  const del = async (aid) => {
    if (!confirm("Eliminare questa nota?")) return;
    try {
      await api.delete(`/clients/${clientId}/activities/${aid}`);
      toast.success("Nota eliminata");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div data-testid="unified-notes" className="col-span-1 md:col-span-2">
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow">{t("clientDetail.notesTitle")}</div>
        <Link to={`/app/clients/${clientId}?tab=activity`}
          onClick={(e) => { e.preventDefault(); const el = document.querySelector('[data-testid="tab-activity"]'); el?.click(); }}
          className="text-[11px] text-[color:var(--sp-brand)] font-bold hover:underline inline-flex items-center gap-1">
          {t("clientDetail.seeFullTimeline")} <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="rounded-xl border border-[color:var(--sp-border)] bg-white p-3 space-y-3">
        <div className="flex gap-2 items-start">
          <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
            data-testid="unified-notes-input"
            className="sp-input flex-1 !min-h-[52px]" placeholder={t("clientDetail.addNoteTimeline")} />
          <button onClick={add} disabled={sending || !text.trim()}
            data-testid="unified-notes-add-btn"
            className="sp-btn-primary self-start inline-flex items-center gap-1.5 whitespace-nowrap">
            <Plus className="h-4 w-4" /> {t("common.add")}
          </button>
        </div>
        {loading ? (
          <div className="text-xs text-[color:var(--sp-text-muted)]">{t("common.loading")}</div>
        ) : notes.length === 0 ? (
          <div className="text-xs text-[color:var(--sp-text-muted)] py-2">{t("clientDetail.noNotes")}</div>
        ) : (
          <ol className="space-y-2">
            {notes.slice(0, 5).map((n) => (
              <li key={n.id} data-testid={`unified-note-${n.id}`}
                className="rounded-lg border border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)]/30 p-2 group hover:border-[color:var(--sp-brand)]/40 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm whitespace-pre-wrap">{n.description}</div>
                    <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">
                      {fmtDateTime(n.created_at)} · {n.actor_name || "Sistema"}
                    </div>
                  </div>
                  <button onClick={() => del(n.id)}
                    data-testid={`unified-note-del-${n.id}`}
                    className="opacity-0 group-hover:opacity-100 h-6 w-6 rounded flex items-center justify-center text-red-600 hover:bg-red-50 transition-opacity">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </li>
            ))}
            {notes.length > 5 && (
              <li className="text-center">
                <button onClick={() => { const el = document.querySelector('[data-testid="tab-activity"]'); el?.click(); }}
                  className="text-[11px] text-[color:var(--sp-brand)] font-bold hover:underline">
                  Vedi le altre {notes.length - 5} note nella timeline →
                </button>
              </li>
            )}
          </ol>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// Activity Timeline (GHL-style)
// ============================================================================
const ACTIVITY_META = {
  contact_created:    { icon: User,          color: "bg-neutral-100 text-neutral-700"  },
  lead_form:          { icon: Facebook,      color: "bg-blue-100 text-blue-700"        },
  appointment_booked: { icon: Calendar,      color: "bg-cyan-100 text-cyan-700"        },
  appointment_updated:{ icon: Calendar,      color: "bg-neutral-100 text-neutral-700"  },
  appointment_completed:{ icon: Check,       color: "bg-emerald-100 text-emerald-700"  },
  appointment_cancelled:{ icon: X,           color: "bg-red-100 text-red-700"          },
  invoice_created:    { icon: Receipt,       color: "bg-amber-100 text-amber-700"      },
  email_sent:         { icon: Mail,          color: "bg-purple-100 text-purple-700"    },
  email_opened:       { icon: Mail,          color: "bg-purple-100 text-purple-700"    },
  email_clicked:      { icon: Mail,          color: "bg-purple-100 text-purple-700"    },
  whatsapp_sent:      { icon: MessageSquare, color: "bg-green-100 text-green-700"      },
  sms_sent:           { icon: Send,          color: "bg-teal-100 text-teal-700"        },
  automation_triggered:{ icon: Zap,          color: "bg-yellow-100 text-yellow-700"    },
  tag_added:          { icon: TagIcon,       color: "bg-indigo-100 text-indigo-700"    },
  tag_removed:        { icon: TagIcon,       color: "bg-neutral-100 text-neutral-500"  },
  pipeline_moved:     { icon: MoveHorizontal, color: "bg-fuchsia-100 text-fuchsia-700" },
  note_added:         { icon: StickyNote,    color: "bg-neutral-100 text-neutral-700"  },
  manual:             { icon: StickyNote,    color: "bg-neutral-100 text-neutral-700"  },
};

function ActivityTimeline({ clientId }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [manualText, setManualText] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = filter ? `/clients/${clientId}/activities?type=${encodeURIComponent(filter)}` : `/clients/${clientId}/activities`;
      const r = await api.get(url);
      setItems(r.data.items || []);
      setTypes(r.data.types || {});
    } catch (e) {
      toast.error("Errore caricamento attività");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, filter]);

  const addNote = async () => {
    const t = manualText.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/clients/${clientId}/activities/manual`, { text: t });
      setManualText("");
      toast.success("Nota aggiunta");
      load();
    } catch (e) {
      toast.error("Errore invio");
    }
    setSending(false);
  };

  const deleteNote = async (aid) => {
    if (!confirm("Eliminare questa nota?")) return;
    try {
      await api.delete(`/clients/${clientId}/activities/${aid}`);
      toast.success("Nota eliminata");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  return (
    <section className="sp-card p-6 space-y-4" data-testid="activity-timeline">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-black">{t("clientDetail.activityTitle")}</h3>
          <p className="text-xs text-[color:var(--sp-text-muted)]">
            Cronologia completa: lead, appuntamenti, tag, pipeline, automazioni.
          </p>
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          data-testid="activity-type-filter"
          className="sp-input max-w-[220px] text-sm">
          <option value="">{t("clientDetail.allTypes")}</option>
          {Object.entries(types).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>

      {/* Add manual note */}
      <div className="flex gap-2 items-start">
        <textarea rows={2} value={manualText} onChange={(e) => setManualText(e.target.value)}
          data-testid="activity-manual-input"
          className="sp-input flex-1" placeholder={t("clientDetail.addNote")} />
        <button onClick={addNote} disabled={sending || !manualText.trim()}
          data-testid="activity-manual-add-btn"
          className="sp-btn-primary self-start inline-flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> {t("common.add")}
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <SkeletonLine key={i} />)}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-sm text-[color:var(--sp-text-muted)]">
          Nessuna attività registrata ancora.
        </div>
      ) : (
        <ol className="relative border-l-2 border-[color:var(--sp-border)] ml-3 space-y-4 pt-2">
          {items.map((a) => <ActivityItem key={a.id} a={a} onDelete={deleteNote} />)}
        </ol>
      )}
    </section>
  );
}

function ActivityItem({ a, onDelete }) {
  const [open, setOpen] = useState(false);
  const meta = ACTIVITY_META[a.type] || ACTIVITY_META.manual;
  const Icon = meta.icon;
  const md = a.metadata || {};
  const canExpand = a.type === "lead_form" || Object.keys(md).length > 3;
  const canDelete = a.type === "manual" && onDelete;

  return (
    <li className="ml-[-14px] pl-8 relative" data-testid={`activity-item-${a.id}`}>
      <div className={`absolute -left-[14px] top-0 h-7 w-7 rounded-full flex items-center justify-center ring-2 ring-white ${meta.color}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="rounded-xl border border-[color:var(--sp-border)] bg-white p-3 hover:border-[color:var(--sp-brand)]/40 transition-colors group">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold">{a.title}</div>
            {a.description && <div className="text-xs text-[color:var(--sp-text-secondary)] mt-0.5 whitespace-pre-wrap">{a.description}</div>}
            <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1 flex items-center gap-2 flex-wrap">
              <span>{fmtDateTime(a.created_at)}</span>
              <span className="text-neutral-300">·</span>
              <span>{a.actor_name || "Sistema"}</span>
              {a.type === "lead_form" && md.platform && (
                <>
                  <span className="text-neutral-300">·</span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-bold uppercase">
                    {md.platform === "instagram" ? <Instagram className="h-3 w-3" /> : md.platform === "web" ? <Globe className="h-3 w-3" /> : <Facebook className="h-3 w-3" />}
                    {md.platform}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            {canDelete && (
              <button onClick={() => onDelete(a.id)}
                data-testid={`activity-delete-${a.id}`}
                title="Elimina nota"
                className="opacity-0 group-hover:opacity-100 h-6 w-6 rounded flex items-center justify-center text-red-600 hover:bg-red-50 transition-opacity">
                <Trash2 className="h-3 w-3" />
              </button>
            )}
            {canExpand && (
              <button onClick={() => setOpen(!open)}
                data-testid={`activity-expand-${a.id}`}
                className="text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)] transition-colors">
                {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>

        {open && a.type === "lead_form" && <LeadTrackingDetails md={md} />}
        {open && a.type !== "lead_form" && (
          <div className="mt-3 pt-3 border-t border-[color:var(--sp-border)]">
            <pre className="text-[10px] bg-neutral-50 p-2 rounded overflow-x-auto text-neutral-600">
              {JSON.stringify(md, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </li>
  );
}

function LeadTrackingDetails({ md }) {
  const { t } = useTranslation();
  const utms = md.utms || {};
  const fd = md.field_data || {};
  return (
    <div className="mt-3 pt-3 border-t border-[color:var(--sp-border)] space-y-3">
      {/* Meta tracking hierarchy */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wide text-[color:var(--sp-text-muted)] mb-1.5">{t("clientDetail.metaTracking")}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          <TrackingRow label="Campagna" value={md.campaign_name} id={md.campaign_id} />
          <TrackingRow label="Adset" value={md.adset_name} id={md.adset_id} />
          <TrackingRow label="Inserzione" value={md.ad_name} id={md.ad_id} />
          <TrackingRow label="Form" value={md.form_name} id={md.form_id} />
        </div>
      </div>

      {/* UTM tracking */}
      {Object.keys(utms).length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-[color:var(--sp-text-muted)] mb-1.5">UTM</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(utms).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-[11px]">
                <span className="font-bold">{k.replace("utm_", "")}</span>
                <span>·</span>
                <span>{v}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Custom form fields */}
      {Object.keys(fd).length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-[color:var(--sp-text-muted)] mb-1.5">Campi form</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
            {Object.entries(fd).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2 px-2 py-1 bg-neutral-50 rounded">
                <span className="text-[color:var(--sp-text-muted)] truncate">{k}</span>
                <span className="font-medium text-right truncate">{v || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {md.page_url && (
        <div className="text-[11px]">
          <span className="text-[color:var(--sp-text-muted)]">Landing page: </span>
          <a href={md.page_url} target="_blank" rel="noreferrer" className="text-[color:var(--sp-brand)] font-medium truncate">{md.page_url}</a>
        </div>
      )}
    </div>
  );
}

function TrackingRow({ label, value, id }) {
  if (!value && !id) return null;
  return (
    <div className="flex flex-col px-2 py-1 bg-neutral-50 rounded">
      <span className="text-[10px] text-[color:var(--sp-text-muted)] uppercase tracking-wide">{label}</span>
      <span className="font-medium truncate">{value || "—"}</span>
      {id && <span className="text-[10px] text-[color:var(--sp-text-muted)] font-mono truncate">{id}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClientPackagesSection — shows all packages this client has bought with an
// expandable per-slot checklist so the staff can tick off each session as it
// is delivered (or watch them auto-tick from completed appointments).
// ---------------------------------------------------------------------------
function ClientPackagesSection({ clientId }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);

  const load = async () => {
    try {
      const [r, ah, sv] = await Promise.all([
        api.get(`/clients/${clientId}/packages`),
        api.get(`/clients/${clientId}/history`).catch(() => ({ data: { appointments: [] } })),
        api.get("/services").catch(() => ({ data: [] })),
      ]);
      setRows(r.data || []);
      const appts = (ah.data?.appointments || []).sort((a, b) => new Date(b.start) - new Date(a.start));
      setAppointments(appts);
      setServices(sv.data || []);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [clientId]);

  const redeemSlot = async (cp, idx) => {
    const key = `${cp.id}-${idx}`;
    setBusy(key);
    try {
      await api.post(`/clients/${clientId}/packages/${cp.id}/slots/${idx}/redeem`);
      toast.success("Sessione riscattata");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(null); }
  };
  const unredeemSlot = async (cp, idx) => {
    const key = `${cp.id}-${idx}`;
    setBusy(key);
    try {
      await api.post(`/clients/${clientId}/packages/${cp.id}/slots/${idx}/unredeem`);
      toast.success("Sessione annullata");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(null); }
  };
  const patchSlot = async (cp, idx, patch) => {
    const key = `${cp.id}-${idx}`;
    setBusy(key);
    try {
      await api.patch(`/clients/${clientId}/packages/${cp.id}/slots/${idx}`, patch);
      toast.success("Seduta aggiornata");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(null); }
  };

  const slotsFor = (cp) => cp.slots || [];
  const isExhausted = (cp) => {
    const s = slotsFor(cp);
    return s.length > 0 && s.every((x) => x.redeemed);
  };

  const active = rows.filter((r) => !isExhausted(r));
  const exhausted = rows.filter((r) => isExhausted(r));

  if (rows.length === 0) {
    return (
      <section className="sp-card p-12 text-center text-[color:var(--sp-text-muted)]" data-testid="client-packages-empty">
        <Gift className="h-8 w-8 mx-auto opacity-40 mb-3" />
        Questo cliente non ha ancora acquistato percorsi. Vendine uno dalla chiusura di un appuntamento.
      </section>
    );
  }

  return (
    <section className="space-y-4" data-testid="client-packages-section">
      {active.length > 0 && (
        <div className="space-y-3">
          <div className="label-eyebrow inline-flex items-center gap-2"><Gift className="h-3.5 w-3.5" /> Attivi ({active.length})</div>
          <div className="grid gap-3">
            {active.map((cp) => (
              <PackageRow
                key={cp.id}
                cp={cp}
                expanded={!!expanded[cp.id]}
                onToggle={() => setExpanded((e) => ({ ...e, [cp.id]: !e[cp.id] }))}
                onRedeem={(idx) => redeemSlot(cp, idx)}
                onUnredeem={(idx) => unredeemSlot(cp, idx)}
                onPatchSlot={(idx, patch) => patchSlot(cp, idx, patch)}
                appointments={appointments}
                services={services}
                busyKey={busy}
              />
            ))}
          </div>
        </div>
      )}
      {exhausted.length > 0 && (
        <div className="space-y-3 pt-4">
          <div className="label-eyebrow text-[color:var(--sp-text-muted)]">Esauriti ({exhausted.length})</div>
          <div className="grid gap-3">
            {exhausted.map((cp) => (
              <PackageRow
                key={cp.id}
                cp={cp}
                expanded={!!expanded[cp.id]}
                onToggle={() => setExpanded((e) => ({ ...e, [cp.id]: !e[cp.id] }))}
                onRedeem={(idx) => redeemSlot(cp, idx)}
                onUnredeem={(idx) => unredeemSlot(cp, idx)}
                onPatchSlot={(idx, patch) => patchSlot(cp, idx, patch)}
                appointments={appointments}
                services={services}
                busyKey={busy}
                exhausted
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function PackageRow({ cp, expanded, onToggle, onRedeem, onUnredeem, onPatchSlot, appointments, services, busyKey, exhausted }) {
  const { t } = useTranslation();
  const slots = cp.slots || [];
  const total = slots.length;
  const used = slots.filter((s) => s.redeemed).length;
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const color = cp.package_color || "#EC4899";
  const purchasedLabel = cp.purchased_at
    ? new Date(cp.purchased_at).toLocaleDateString(locale(), { day: "2-digit", month: "long", year: "numeric" })
    : "—";
  return (
    <div data-testid={`client-package-${cp.id}`}
         className={`sp-card p-5 relative overflow-hidden ${exhausted ? "opacity-70" : ""}`}>
      <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-10 pointer-events-none" style={{ background: color, transform: "translate(30%, -30%)" }} />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <button type="button" onClick={onToggle} className="flex-1 min-w-[220px] text-left group" data-testid={`client-package-toggle-${cp.id}`}>
          <div className="inline-flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
            <span className="label-eyebrow" style={{ color }}>{exhausted ? "Percorso esaurito" : "Percorso attivo"}</span>
            {expanded
              ? <ChevronDown className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />
              : <ChevronRight className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />}
          </div>
          <div className={`font-display text-xl font-black mt-1 group-hover:text-[color:var(--sp-brand)] transition-colors ${exhausted ? "line-through" : ""}`}>{cp.package_name}</div>
          <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1">Acquistato il {purchasedLabel}</div>
        </button>
      </div>
      <div className="mt-4">
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xs text-[color:var(--sp-text-secondary)]">
            <b className="text-2xl font-display font-black" style={{ color }} data-testid={`client-package-remaining-${cp.id}`}>{remaining}</b>
            <span className="ml-1">di {total} sessioni rimaste</span>
          </span>
          <span className="text-xs font-bold text-[color:var(--sp-text-muted)] tabular-nums">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-[color:var(--sp-subtle)] overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} data-testid={`client-package-bar-${cp.id}`} />
        </div>
      </div>
      {expanded && (
        <div className="mt-4 pt-4 border-t border-[color:var(--sp-border)] space-y-2" data-testid={`client-package-slots-${cp.id}`}>
          <div className="label-eyebrow mb-1">Sedute incluse ({total})</div>
          {slots.length === 0 && <div className="text-xs text-[color:var(--sp-text-muted)]">{t("clientDetail.noSessions")}</div>}
          {slots.map((slot, i) => (
            <SlotRow
              key={i}
              cpId={cp.id}
              slot={slot}
              idx={i}
              onRedeem={() => onRedeem(i)}
              onUnredeem={() => onUnredeem(i)}
              onPatch={(patch) => onPatchSlot(i, patch)}
              appointments={appointments}
              services={services}
              busy={busyKey === `${cp.id}-${i}`}
              color={color}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SlotRow({ cpId, slot, idx, onRedeem, onUnredeem, onPatch, appointments, busy, color, services }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(slot.service_name || "");
  const [date, setDate] = useState(slot.redeemed_at ? slot.redeemed_at.slice(0, 10) : "");
  const [apptId, setApptId] = useState(slot.appointment_id || "");

  const startEdit = () => {
    setName(slot.service_name || "");
    setDate(slot.redeemed_at ? slot.redeemed_at.slice(0, 10) : "");
    setApptId(slot.appointment_id || "");
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);
  const saveEdit = async () => {
    const patch = {};
    if (name !== slot.service_name) patch.service_name = name;
    if (date && date !== (slot.redeemed_at || "").slice(0, 10)) patch.redeemed_at = `${date}T00:00:00Z`;
    if (apptId && apptId !== slot.appointment_id) patch.appointment_id = apptId;
    else if (!apptId && slot.appointment_id) patch.appointment_id = null;
    if (Object.keys(patch).length === 0) { setEditing(false); return; }
    await onPatch(patch);
    setEditing(false);
  };

  const redeemedAt = slot.redeemed_at
    ? new Date(slot.redeemed_at).toLocaleDateString(locale(), { day: "2-digit", month: "short", year: "2-digit" })
    : null;

  if (editing) {
    // Sort appts by start desc, only those of this client (parent filters)
    const opts = (appointments || []).map((a) => ({
      id: a.id,
      label: new Date(a.start).toLocaleString(locale(), { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }),
      sub: (a.service_ids || []).map((sid) => services?.find((s) => s.id === sid)?.name).filter(Boolean).join(" · ") || "—",
    }));
    return (
      <div className="rounded-lg border-2 border-[color:var(--sp-brand)] bg-white p-3 space-y-2" data-testid={`client-package-slot-editor-${cpId}-${idx}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] label-eyebrow">Nome servizio</label>
            <input data-testid={`slot-edit-name-${cpId}-${idx}`} value={name} onChange={(e) => setName(e.target.value)} className="sp-input !py-1 text-xs mt-0.5" />
          </div>
          <div>
            <label className="text-[10px] label-eyebrow">Data erogazione</label>
            <input data-testid={`slot-edit-date-${cpId}-${idx}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className="sp-input !py-1 text-xs mt-0.5" />
          </div>
        </div>
        <div>
          <label className="text-[10px] label-eyebrow">Collega ad appuntamento (opzionale)</label>
          <SearchableSelect
            testId={`slot-edit-appt-${cpId}-${idx}`}
            size="sm"
            value={apptId}
            onChange={setApptId}
            options={opts}
            placeholder={opts.length ? "— Nessun appuntamento —" : "Nessun appuntamento del cliente"}
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={cancelEdit} className="text-[11px] px-2.5 py-1 rounded-full border border-[color:var(--sp-border)]" data-testid={`slot-edit-cancel-${cpId}-${idx}`}>Annulla</button>
          <button type="button" onClick={saveEdit} disabled={busy} className="text-[11px] px-2.5 py-1 rounded-full bg-[color:var(--sp-brand)] text-white font-bold" data-testid={`slot-edit-save-${cpId}-${idx}`}>Salva</button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`client-package-slot-${cpId}-${idx}`}
      className={`flex items-center gap-3 rounded-lg border p-2.5 transition-colors ${slot.redeemed ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-[color:var(--sp-border)]"}`}
    >
      <button
        type="button"
        onClick={slot.redeemed ? onUnredeem : onRedeem}
        disabled={busy}
        data-testid={`client-package-slot-check-${cpId}-${idx}`}
        aria-label={slot.redeemed ? "Annulla riscatto" : "Marca come riscattato"}
        className={`h-5 w-5 rounded-md flex-shrink-0 flex items-center justify-center transition-all border-2 ${slot.redeemed ? "border-emerald-500 bg-emerald-500 text-white" : "border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)]"}`}
      >
        {slot.redeemed && <Check className="h-3 w-3" strokeWidth={4} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-bold ${slot.redeemed ? "text-emerald-800" : ""}`}>{slot.service_name || "Servizio"}</div>
        {slot.redeemed && (
          <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-0.5 inline-flex items-center gap-2 flex-wrap">
            <span>Erogato il <b>{redeemedAt}</b></span>
            {slot.appointment_id && (
              <a
                href={`/app/agenda?edit=${slot.appointment_id}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-0.5 text-[color:var(--sp-brand)] hover:underline"
                data-testid={`client-package-slot-appt-link-${cpId}-${idx}`}
              >
                <Calendar className="h-2.5 w-2.5" /> vedi appuntamento
              </a>
            )}
            {slot.manual && !slot.appointment_id && <span className="italic">(check manuale)</span>}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={startEdit}
        className="p-1 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-brand)]"
        data-testid={`client-package-slot-edit-${cpId}-${idx}`}
        title="Modifica seduta e collegamento appuntamento"
      >
        <Pencil className="h-3 w-3" />
      </button>
      {!slot.redeemed && (
        <button
          type="button"
          onClick={onRedeem}
          disabled={busy}
          data-testid={`client-package-slot-redeem-${cpId}-${idx}`}
          className="text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap"
          style={{ background: `${color}18`, color }}
        >
          Riscatta
        </button>
      )}
    </div>
  );
}

